import type { SponsorCampaign } from "../campaignTypes";

/**
 * Performance figures are real, taken from the four taping reports plus
 * Snapchat Public Stories / Spotlight reporting.
 *
 * Reconciliation note: each sheet's TOTAL column equals views plus all
 * engagement actions (likes, comments, shares, saves, reposts). The values
 * below were checked against that total for all four tapings.
 *
 * Snapchat views are reported at the campaign level only (Public Stories +
 * Spotlight). Per-taping Snapchat splits are not available.
 */
export const drSquatchCampaign: SponsorCampaign = {
  campaignSlug: "dr-squatch",
  sponsorName: "Dr. Squatch",
  sponsorLogo: "/images/brand-dr-squatch-header.png",
  sponsorWordmark: "dr. squatch",
  comedyuoLogo: "/images/Comedy_UO_logo_whiteversion_edited.png",
  accentColor: "#2F7A3E",
  eyebrow: "Dr. Squatch × ComedyUO",
  campaignName: "Dr. Squatch x ComedyUO Four-Taping Season",
  shareTitle: "Dr. Squatch 4 Taping Series Recap",
  shareImage: "https://storage.googleapis.com/cuo-website/Head%20Image.jpg",
  headline: "Four Rooms, Lots of Soap,",
  // Source-of-truth for regenerating server JSON; not imported by the public site bundle.
  // See campaigns/index.ts for the export command.
  headlineLine2: "and Even More Laughs",
  venue: "Four NYC venues",
  dateRange: "August 2025 - August 2026",
  eventDate: "August 2025 - August 2026",
  reportingPeriod: "Through August 17, 2026",
  campaignSummary:
    "A four-taping season for Dr. Squatch across four unexpected New York rooms: a vintage furniture warehouse, a smokehouse, a distillery, and a sporting goods store. The live audiences didn't disappoint, and neither did the digital. See the results below!",
  heroImage: "https://storage.googleapis.com/cuo-website/Head%20Image.jpg",
  heroImageAlt:
    "ComedyUO hosts with Dr. Squatch product during the campaign season",
  heroCtaLabel: "View Deliverables",
  heroCtaHref: "#delivered",
  featuredMetric: {
    label: "Total views",
    value: "22.4M",
    detail:
      "22,415,279 across all tapings and channels, including 13,610,832 from Snapchat and YouTube compilations",
  },
  glanceFields: [
    {
      id: "deal",
      label: "Partnership type",
      value: "Presenting sponsor, four-taping season package",
    },
    {
      id: "investment",
      label: "Total investment",
      value: "$65,000",
    },
    {
      id: "cpm",
      label: "CPM",
      // $65,000 / 22,415,279 * 1,000 = $2.90
      value: "$2.90 per thousand views",
    },
    {
      id: "tapings",
      label: "Tapings",
      value: "4 tapings, 1 live audience each",
    },
    {
      id: "posts",
      label: "Posts published",
      value: "255 across Instagram, TikTok, and YouTube",
    },
    {
      id: "period",
      label: "Campaign period",
      value: "12 months",
    },
  ],
  activationsSection: {
    eyebrow: "The work",
    title: "Four tapings, four rooms",
    description:
      "Every taping in the sponsorship with its own Instagram, TikTok, and YouTube delivery numbers. Snapchat is reported at the campaign level only.",
  },
  activations: [
    {
      id: "furnish-green",
      number: 1,
      title: "Furnish Green",
      date: "August 14, 2025",
      location: "Furnish Green, NYC",
      talent:
        "Gabby Bryan, Kevin Dombrowski, Olivia Carter, Ahri Findling, Reg Thomas, Brian Scolaro, Peter Revello, Lizzie Cassidy, Ian Lara, Jason Choi, Leonard Outsz",
      description:
        "Season opener inside a vintage furniture warehouse. The room set the tone for the whole package and delivered the second-highest Instagram Reels view count of the four tapings.",
      metrics: [
        { id: "views", label: "Views", value: "2,431,768" },
        { id: "eng", label: "Engagements", value: "153,277" },
        { id: "posts", label: "Posts", value: "63" },
      ],
      photos: [
        {
          id: "p1",
          src: "https://storage.googleapis.com/cuo-website/Furnish%20Extra.jpg",
          alt: "The Furnish Green room during the Dr. Squatch taping",
        },
        {
          id: "p2",
          src: "https://storage.googleapis.com/cuo-website/Gabby%20Bryan%20Furnish.jpg",
          alt: "Comic performing on stage at the Furnish Green taping",
        },
        {
          id: "p3",
          src: "https://storage.googleapis.com/cuo-website/Ian%20Lara%20Furnish.jpg",
          alt: "Ian Lara performing at the Furnish Green taping",
        },
      ],
    },
    {
      id: "acme-smoked-fish",
      number: 2,
      title: "ACME Smoked Fish",
      date: "October 10, 2025",
      location: "ACME Smoked Fish, Brooklyn",
      talent:
        "Gabby Bryan, Eva Evans, Tommy Brennan, Peter Revello, Sherrod Small",
      description:
        "The biggest night of the season on every measure. ACME drove 3.8M views and 292,773 engagement actions, roughly 44 percent of the campaign's Instagram, TikTok, and YouTube reach on its own.",
      metrics: [
        { id: "views", label: "Views", value: "3,827,191" },
        { id: "eng", label: "Engagements", value: "292,773" },
        { id: "posts", label: "Posts", value: "96" },
      ],
      photos: [
        {
          id: "p1",
          src: "https://storage.googleapis.com/cuo-website/Jourdain%20Fischer%20ACME.jpg",
          alt: "Comic performing at the ACME Smoked Fish taping",
        },
        {
          id: "p2",
          src: "https://storage.googleapis.com/cuo-website/ACME%20Squatch.jpg",
          alt: "Dr. Squatch branding on set at the ACME Smoked Fish taping",
        },
        {
          id: "p3",
          src: "https://storage.googleapis.com/cuo-website/ACME%20Squatch%202.jpg",
          alt: "Live audience in front of the branded set at the ACME Smoked Fish taping",
        },
      ],
    },
    {
      id: "great-jones",
      number: 3,
      title: "Great Jones Distillery",
      date: "February 5, 2026",
      location: "Great Jones Distillery, NYC",
      talent:
        "Tina Friml, Stewart Fullerton, Nathan Macintosh, Maddy Smith, Kevin Dombrowski, Geoffrey Asmus, Eric Neumann, Derek Gaines, Dan Naturman, Asha Ward, Andy Hanes",
      description:
        "The most efficient room of the season. Great Jones posted a 9.6 percent engagement rate, the highest of the four, from four Instagram Reels posts.",
      metrics: [
        { id: "views", label: "Views", value: "1,448,234" },
        { id: "eng", label: "Engagements", value: "126,793" },
        { id: "posts", label: "Posts", value: "65" },
      ],
      photos: [
        {
          id: "p1",
          src: "https://storage.googleapis.com/cuo-website/Eric%20Nueman%20Great%20Jones.jpg",
          alt: "Eric Neumann performing at the Great Jones Distillery taping",
        },
        {
          id: "p2",
          src: "https://storage.googleapis.com/cuo-website/Great%20Jones%20Squatch.jpg",
          alt: "Dr. Squatch branding on set at the Great Jones Distillery taping",
        },
        {
          id: "p3",
          src: "https://storage.googleapis.com/cuo-website/Squatch%20Product.jpg",
          alt: "Dr. Squatch product close-up from the Great Jones taping",
        },
      ],
    },
    {
      id: "paragon-sports",
      number: 4,
      title: "Paragon Sports",
      date: "April 29 - May 2, 2026",
      location: "Paragon Sports, NYC",
      talent:
        "Ray, Philipp Kostelecky, Peter Revello, Ola, Marie, Lizzie Cassidy, Lev Fer, Kevin Dombrowski, Ian Lara, Erin, Elle",
      description:
        "Season finale on the sporting goods floor. The smallest publishing run of the package at 31 posts, still clearing 953,342 views.",
      metrics: [
        { id: "views", label: "Views", value: "953,342" },
        { id: "eng", label: "Engagements", value: "27,826" },
        { id: "posts", label: "Posts", value: "31" },
      ],
      photos: [
        {
          id: "p1",
          src: "https://storage.googleapis.com/cuo-website/Paragon%20Squatch%201.jpg",
          alt: "Comic on stage beside the Paragon and Dr. Squatch signage",
        },
        {
          id: "p2",
          src: "https://storage.googleapis.com/cuo-website/Paragon%20Squatch%202.jpg",
          alt: "Comic performing in front of the sneaker wall at Paragon Sports",
        },
        {
          id: "p3",
          src: "https://storage.googleapis.com/cuo-website/Paragon%20Squatch%203.jpg",
          alt: "Full audience watching the closing set on the Paragon Sports floor",
        },
      ],
    },
  ],
  platformPerformance: {
    eyebrow: "The numbers",
    title: "Views by platform",
    description:
      "Campaign totals across every channel. Snapchat leads at 61 percent. Instagram, TikTok, and YouTube are measured per taping, plus YouTube compilations.",
    rows: [
      {
        id: "snap",
        platform: "Snapchat",
        contentNote: "Public Stories and Spotlight, campaign-level",
        impressions: "13,610,832",
        impressionsRaw: 13610832,
        sharePercent: 61,
      },
      {
        id: "ig",
        platform: "Instagram Reels",
        contentNote: "59 posts across four tapings",
        impressions: "6,307,801",
        impressionsRaw: 6307801,
        sharePercent: 28,
      },
      {
        id: "yts",
        platform: "YouTube Shorts",
        contentNote: "84 shorts",
        impressions: "1,192,815",
        impressionsRaw: 1192815,
        sharePercent: 5,
      },
      {
        id: "ytf",
        platform: "YouTube (full sets)",
        contentNote: "32 full sets plus compilations",
        impressions: "884,703",
        impressionsRaw: 884703,
        sharePercent: 4,
      },
      {
        id: "tt",
        platform: "TikTok",
        contentNote: "80 posts",
        impressions: "419,128",
        impressionsRaw: 419128,
        sharePercent: 2,
      },
    ],
    metricColumnLabel: "Views",
    totalImpressionsLabel: "Total views",
    totalImpressionsValue: "22,415,279",
    secondaryStat: {
      label: "Snapchat views",
      value: "13,610,832",
      detail: "Campaign-level Snapchat views. Not split by taping.",
    },
  },
  kpiSection: {
    eyebrow: "The numbers",
    title: "How the campaign performed",
    description:
      "Platform-reported totals across all four tapings, plus Snapchat campaign attribution. Measured figures come straight from the platform reports. Calculated and modeled figures are labelled.",
    groups: [
      {
        id: "reach",
        heading: "Reach",
        metrics: [
          {
            id: "views",
            label: "Total views",
            value: "22,415,279",
            provenance: "calculated",
            explanation:
              "Total across all tapings and channels: Instagram, TikTok, YouTube, YouTube compilations, and Snapchat. Each taping TOTAL includes views plus engagement actions.",
          },
          {
            id: "snapchat",
            label: "Snapchat views",
            value: "13,610,832",
            provenance: "modeled",
            vsGoalLabel: "61% of total reach",
            vsGoalPositive: true,
            explanation:
              "Campaign-level Snapchat Public Stories and Spotlight views. Not split by taping.",
          },
          {
            id: "social-views",
            label: "IG / TikTok / YouTube views",
            value: "8,804,447",
            provenance: "measured",
            explanation:
              "Four taping TOTALs plus YouTube compilations. Snapchat is counted separately.",
          },
          {
            id: "views-plus",
            label: "Views plus engagements",
            value: "22,415,279",
            provenance: "calculated",
            explanation:
              "Same as the total across all tapings and channels, including Snapchat.",
          },
          {
            id: "posts",
            label: "Posts published",
            value: "255",
            provenance: "measured",
            explanation: "Instagram, TikTok, and YouTube taping posts only.",
          },
        ],
      },
      {
        id: "engagement",
        heading: "Engagement",
        metrics: [
          {
            id: "engagements",
            label: "Total engagements",
            value: "600,669",
            provenance: "calculated",
            explanation:
              "Likes, comments, shares, saves, and reposts across Instagram, TikTok, and YouTube. Snapchat engagement actions were not included in this total.",
          },
          {
            id: "er",
            label: "Engagement rate",
            value: "7.5%",
            provenance: "calculated",
            provenanceLabel: "Calculated without Snap #s",
            explanation:
              "Total engagements divided by Instagram, TikTok, and YouTube views (8,059,866). Snapchat views are excluded because people cannot engage with Snap content the same way, and Snap engagement actions were not reported.",
          },
          {
            id: "likes",
            label: "Likes",
            value: "418,750",
            provenance: "measured",
          },
          {
            id: "shares",
            label: "Shares",
            value: "127,401",
            provenance: "measured",
            explanation: "Instagram Reels, TikTok, and YouTube.",
          },
          {
            id: "saves",
            label: "Saves",
            value: "41,668",
            provenance: "measured",
            explanation: "Instagram Reels and TikTok only.",
          },
          {
            id: "comments",
            label: "Comments",
            value: "6,422",
            provenance: "measured",
          },
        ],
      },
      {
        id: "by-taping",
        heading: "Strongest rooms",
        metrics: [
          {
            id: "top-reach",
            label: "Highest reach taping",
            value: "ACME Smoked Fish",
            provenance: "measured",
            explanation:
              "3,827,191 Instagram, TikTok, and YouTube total, about 44 percent of that channel group.",
          },
          {
            id: "top-er",
            label: "Highest engagement rate",
            value: "Great Jones",
            provenance: "measured",
            explanation: "9.6 percent engagement rate against views.",
          },
        ],
      },
    ],
  },
  gallery: {
    eyebrow: "The night",
    title: "Photos and Media",
    description:
      "Two kinds of folders for each taping: photo galleries in Google Drive, and full taping files in Dropbox.",
    images: [
      {
        id: "g-furnish-green",
        src: "https://storage.googleapis.com/cuo-website/Furnish%20Extra.jpg",
        alt: "The Furnish Green room during the Dr. Squatch taping",
        caption: "Furnish Green",
        badge: "Photo gallery",
        href: "https://drive.google.com/drive/folders/1yMIBn9tOchGiYHmecfr9wVx-nEN-VI7i?usp=sharing",
      },
      {
        id: "m-furnish-green",
        src: "https://storage.googleapis.com/cuo-website/Gabby%20Bryan%20Furnish.jpg",
        alt: "Furnish Green taping set files",
        caption: "Furnish Green",
        badge: "Taping files",
        href: "https://www.dropbox.com/scl/fo/jangagpwjdnknqs84bl96/AJj8VJoQ35rljp48bHQ3j0o/Furnish%20Green%20Taping%208-14-25?rlkey=qz5i3zgm8sd1h5ztyuqlo3z57&dl=0",
      },
      {
        id: "g-acme",
        src: "https://storage.googleapis.com/cuo-website/ACME%20Squatch.jpg",
        alt: "Dr. Squatch branding on set at the ACME Smoked Fish taping",
        caption: "ACME Smoked Fish",
        badge: "Photo gallery",
        href: "https://drive.google.com/drive/folders/1J52efnDJT7cdDyxHuSP2nG65ExxJ5ODT?usp=sharing",
      },
      {
        id: "m-acme",
        src: "https://storage.googleapis.com/cuo-website/ACME%20Squatch%202.jpg",
        alt: "ACME Smoked Fish taping set files",
        caption: "ACME Smoked Fish",
        badge: "Taping files",
        href: "https://www.dropbox.com/scl/fo/jangagpwjdnknqs84bl96/AMLvjLFuwnjvg2ND2cx8eTA/ACME%20Taping%2010-10-25?rlkey=qz5i3zgm8sd1h5ztyuqlo3z57&dl=0",
      },
      {
        id: "g-great-jones",
        src: "https://storage.googleapis.com/cuo-website/Great%20Jones%20Squatch.jpg",
        alt: "Dr. Squatch branding on set at the Great Jones Distillery taping",
        caption: "Great Jones Distillery",
        badge: "Photo gallery",
        href: "https://drive.google.com/drive/folders/1FeAYjsbnVpV-FnuY2skjpxWZCEnNRE3F?usp=sharing",
      },
      {
        id: "m-great-jones",
        src: "https://storage.googleapis.com/cuo-website/Eric%20Nueman%20Great%20Jones.jpg",
        alt: "Great Jones Distillery taping set files",
        caption: "Great Jones Distillery",
        badge: "Taping files",
        href: "https://www.dropbox.com/scl/fo/4gihvprys5mj69zpy2cfx/AElhoy_xOeirH26tX6LOfwE/Great%20Jones%20Taping%202-5-26?rlkey=11zqjy99vhzv025mt33rsps81&dl=0",
      },
      {
        id: "g-paragon",
        src: "https://storage.googleapis.com/cuo-website/Paragon%20Squatch%201.jpg",
        alt: "Comic on stage beside the Paragon and Dr. Squatch signage",
        caption: "Paragon Sports",
        badge: "Photo gallery",
        href: "https://drive.google.com/drive/folders/17yjHSiUatrGLuVpdjyZ0egIYNB5e-2iz?usp=sharing",
      },
      {
        id: "m-paragon",
        src: "https://storage.googleapis.com/cuo-website/Paragon%20Squatch%202.jpg",
        alt: "Paragon Sports taping set files",
        caption: "Paragon Sports",
        badge: "Taping files",
        href: "https://www.dropbox.com/scl/fo/4gihvprys5mj69zpy2cfx/AIJufx2AEsWkVtqQYd5N1rY/Paragon%20Taping%204-30-26?rlkey=11zqjy99vhzv025mt33rsps81&dl=0",
      },
    ],
  },
  assets: {
    eyebrow: "The report",
    title: "Download this report as a PDF",
    description:
      "A print-ready version of this portal with the same numbers, tapings, and platform breakdown.",
    reportPdf: true,
    items: [],
  },
  closing: {
    thankYouHeadline: "Thank you, Dr. Squatch",
    summary:
      "Four rooms, four audiences, and a content run that kept working long after the last taping wrapped. Thank you for backing the whole season instead of a single night.",
    highlightCallout:
      "22,415,279 views including 13.61M from Snapchat, plus 600,669 engagement actions across 255 posts at a 7.5 percent engagement rate on Instagram, TikTok, and YouTube.",
    contact: {
      name: "Mike Zbyszewski",
      role: "Sales & Partnerships, ComedyUO",
      email: "mike@comedyuo.com",
    },
  },
  nav: [
    { id: "overview", label: "Overview", href: "#overview" },
    { id: "delivered", label: "Delivered", href: "#delivered" },
    { id: "performance", label: "Performance", href: "#performance" },
    { id: "gallery", label: "Photos", href: "#gallery" },
    { id: "assets", label: "Report", href: "#assets" },
  ],
  isSamplePlaceholder: false,
  sampleDisclaimer: "",
  allowedEmailDomains: ["drsquatch.com", "comedyuo.com"],
};
